// Grafo force-directed de la memoria gobernada — SVG interactivo, sin librería.
// Estética "AI-native" THEME-AWARE (respeta data-theme claro/oscuro de <html>):
// stage con gradiente + trama sutil, nodos con halo suave, aristas refinadas,
// asentamiento animado en vivo y focus-on-hover. Pinta EXACTAMENTE lo que el
// cerebro devuelve. Paletas de clusters VALIDADAS (CVD-safe) para ambos modos.

import { useEffect, useMemo, useRef, useState } from 'react';
import type { GraphResponse, MemoryNode, Clasif } from '../api';

type Mode = 'light' | 'dark';

// Lee el tema de <html data-theme> y reacciona al toggle.
function useThemeMode(): Mode {
  const read = (): Mode =>
    (typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'light')
      ? 'light' : 'dark';
  const [mode, setMode] = useState<Mode>(read);
  useEffect(() => {
    const obs = new MutationObserver(() => setMode(read()));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    setMode(read());
    return () => obs.disconnect();
  }, []);
  return mode;
}

// Paletas VALIDADAS (validate_palette.js ALL PASS en su modo) — orden fijo.
const CLASIF: Record<Mode, Record<Clasif, string>> = {
  dark:  { publica: '#34d399', interna: '#60a5fa', restringida: '#fbbf24', confidencial: '#f87171' },
  light: { publica: '#059669', interna: '#2563eb', restringida: '#d97706', confidencial: '#dc2626' },
};
const CLUSTER: Record<Mode, string[]> = {
  dark:  ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#008300', '#9085e9', '#e66767'],
  light: ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'],
};
const cluster = (mode: Mode, id?: number) =>
  id == null ? (mode === 'dark' ? '#64748b' : '#94a3b8') : CLUSTER[mode][id % CLUSTER[mode].length];

// Tokens del stage por tema.
const T: Record<Mode, {
  stage: [string, string, string]; dot: string; text: string; textDim: string;
  edge: string; edgeSoft: string; org: string; glass: string; ring: string; shadow: string; nodeStroke: string;
}> = {
  dark: {
    stage: ['#141d31', '#0b1220', '#060912'], dot: '#1e293b', text: '#cbd5e1', textDim: '#475569',
    edge: '#334155', edgeSoft: '#3f4a5c', org: '#334155',
    glass: 'bg-white/5 border-white/10 text-slate-200', ring: 'border-slate-800/60',
    shadow: '0 20px 60px -20px rgba(0,0,0,0.6)', nodeStroke: 'rgba(255,255,255,0.55)',
  },
  light: {
    stage: ['#ffffff', '#f1f5f9', '#e7edf5'], dot: '#cbd5e1', text: '#334155', textDim: '#94a3b8',
    edge: '#cbd5e1', edgeSoft: '#dbe2ea', org: '#64748b',
    glass: 'bg-black/5 border-black/10 text-slate-700', ring: 'border-slate-200',
    shadow: '0 12px 40px -16px rgba(15,23,42,0.18)', nodeStroke: 'rgba(255,255,255,0.85)',
  },
};

const REL_COLOR: Record<string, { stroke: (t: typeof T[Mode]) => string; w: number; dash?: string; glow?: boolean; strong?: boolean }> = {
  belongs_to:     { stroke: (t) => t.edge, w: 1 },
  extracted_from: { stroke: () => '#3b82f6', w: 1.3 },
  spoken_in:      { stroke: () => '#10b981', w: 1.3 },
  fed_by:         { stroke: () => '#8b7fd6', w: 1, dash: '2 4' },
  governed_by:    { stroke: (t) => t.edgeSoft, w: 1 },
  corrects:       { stroke: () => '#f59e0b', w: 2.2, glow: true, strong: true },
  similar_to:     { stroke: () => '#ec4899', w: 1.8, glow: true, strong: true },
};

interface P { x: number; y: number }
const W = 900, H = 580;

function initPositions(nodes: MemoryNode[]): P[] {
  return nodes.map((_, i) => ({
    x: W / 2 + Math.cos(i * 2.399) * (40 + (i % 9) * 6),
    y: H / 2 + Math.sin(i * 2.399) * (40 + (i % 7) * 6),
  }));
}

function tick(pos: P[], links: { s: number; t: number; sim: boolean }[], k: number, pinned: number | null) {
  const n = pos.length;
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      let dx = pos[i].x - pos[j].x, dy = pos[i].y - pos[j].y;
      const d2 = dx * dx + dy * dy || 0.01, d = Math.sqrt(d2);
      const rep = 2800 / d2, fx = (dx / d) * rep * k, fy = (dy / d) * rep * k;
      if (i !== pinned) { pos[i].x += fx; pos[i].y += fy; }
      if (j !== pinned) { pos[j].x -= fx; pos[j].y -= fy; }
    }
  }
  for (const l of links) {
    let dx = pos[l.t].x - pos[l.s].x, dy = pos[l.t].y - pos[l.s].y;
    const d = Math.sqrt(dx * dx + dy * dy) || 0.01;
    const target = l.sim ? 58 : 100, stiff = l.sim ? 0.07 : 0.03;
    const f = (d - target) * stiff * k, fx = (dx / d) * f, fy = (dy / d) * f;
    if (l.s !== pinned) { pos[l.s].x += fx; pos[l.s].y += fy; }
    if (l.t !== pinned) { pos[l.t].x -= fx; pos[l.t].y -= fy; }
  }
  for (let i = 0; i < n; i++) {
    if (i === pinned) continue;
    pos[i].x += (W / 2 - pos[i].x) * 0.006 * k;
    pos[i].y += (H / 2 - pos[i].y) * 0.006 * k;
  }
}

export function MemoryGraphCanvas({ data, onSelect, colorBy = 'clasif' }: {
  data: GraphResponse; onSelect: (n: MemoryNode) => void; colorBy?: 'clasif' | 'cluster';
}) {
  const mode = useThemeMode();
  const t = T[mode];
  const posRef = useRef<P[]>(initPositions(data.nodes));
  const [, force] = useState(0);
  const rerender = () => force((f) => f + 1);
  const [view, setView] = useState({ x: 0, y: 0, k: 1 });
  const [hover, setHover] = useState<number | null>(null);
  const drag = useRef({ node: null as number | null, panning: false, sx: 0, sy: 0, moved: false });
  const svgRef = useRef<SVGSVGElement>(null);

  const idx = useMemo(() => {
    const m: Record<string, number> = {};
    data.nodes.forEach((n, i) => { m[n.id] = i; });
    return m;
  }, [data]);
  const links = useMemo(() =>
    data.edges.map((e) => ({ s: idx[e.source], t: idx[e.target], sim: e.rel === 'similar_to' }))
      .filter((l) => l.s != null && l.t != null && l.s !== l.t), [data, idx]);
  const maxOcc = useMemo(() => Math.max(1, ...data.nodes.map((n) => n.occurrences ?? 1)), [data]);
  const neighbors = useMemo(() => {
    const m: Record<number, Set<number>> = {};
    data.nodes.forEach((_, i) => { m[i] = new Set([i]); });
    for (const l of links) { m[l.s].add(l.t); m[l.t].add(l.s); }
    return m;
  }, [data, links]);

  useEffect(() => {
    posRef.current = initPositions(data.nodes);
    let raf = 0, frames = 0, cool = 1;
    const loop = () => {
      for (let s = 0; s < 2; s++) tick(posRef.current, links, cool, drag.current.node);
      cool *= 0.984; frames++; rerender();
      if (frames < 200) raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [data, links]);

  const pos = posRef.current;

  function toWorld(cx: number, cy: number) {
    const r = svgRef.current!.getBoundingClientRect();
    const sx = (cx - r.left) / r.width * W, sy = (cy - r.top) / r.height * H;
    return { x: (sx - view.x) / view.k, y: (sy - view.y) / view.k };
  }
  function onMove(e: any) {
    if (drag.current.node != null) {
      const w = toWorld(e.clientX, e.clientY);
      pos[drag.current.node] = w; drag.current.moved = true; rerender();
    } else if (drag.current.panning) {
      const dx = e.clientX - drag.current.sx, dy = e.clientY - drag.current.sy;
      drag.current.sx = e.clientX; drag.current.sy = e.clientY;
      setView((v) => ({ ...v, x: v.x + dx, y: v.y + dy }));
    }
  }
  function onWheel(e: any) {
    e.preventDefault();
    setView((v) => ({ ...v, k: Math.max(0.4, Math.min(3, v.k * (e.deltaY < 0 ? 1.12 : 0.89))) }));
  }

  if (pos.length !== data.nodes.length) return null;
  const focus = hover != null ? neighbors[hover] : null;
  // Con muchos nodos, el filtro de glow (feGaussianBlur) por nodo es caro:
  // se apaga y se conserva solo el halo translúcido (barato).
  const heavy = data.nodes.length > 70;

  return (
    <div className={`relative rounded-2xl overflow-hidden border ${t.ring}`} style={{ boxShadow: t.shadow }}>
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} className="w-full touch-none select-none"
        style={{ height: 580, cursor: drag.current.panning ? 'grabbing' : 'grab', display: 'block' }}
        onMouseDown={(e) => { drag.current.panning = true; drag.current.sx = e.clientX; drag.current.sy = e.clientY; }}
        onMouseUp={() => { drag.current.node = null; drag.current.panning = false; }}
        onMouseLeave={() => { drag.current.node = null; drag.current.panning = false; setHover(null); }}
        onMouseMove={onMove} onWheel={onWheel}>
        <defs>
          <radialGradient id="mg-stage" cx="50%" cy="38%" r="80%">
            <stop offset="0%" stopColor={t.stage[0]} />
            <stop offset="55%" stopColor={t.stage[1]} />
            <stop offset="100%" stopColor={t.stage[2]} />
          </radialGradient>
          <filter id="mg-glow" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="4" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
          <pattern id="mg-dots" width="26" height="26" patternUnits="userSpaceOnUse">
            <circle cx="1" cy="1" r="1" fill={t.dot} opacity="0.55" />
          </pattern>
        </defs>

        <rect x={-2000} y={-2000} width={5000} height={5000} fill="url(#mg-stage)" />
        <rect x={-2000} y={-2000} width={5000} height={5000} fill="url(#mg-dots)" opacity={0.5} />

        <g transform={`translate(${view.x} ${view.y}) scale(${view.k})`}>
          {data.edges.map((e, i) => {
            const a = pos[idx[e.source]], b = pos[idx[e.target]];
            if (!a || !b) return null;
            const st = REL_COLOR[e.rel] || REL_COLOR.belongs_to;
            const dim = focus && !(focus.has(idx[e.source]) && focus.has(idx[e.target]));
            return (
              <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                stroke={st.stroke(t)} strokeWidth={st.w} strokeDasharray={st.dash} strokeLinecap="round"
                opacity={dim ? 0.06 : st.strong ? 0.85 : mode === 'dark' ? 0.4 : 0.55}
                filter={st.glow && !dim ? 'url(#mg-glow)' : undefined} />
            );
          })}
          {data.nodes.map((n, i) => {
            const p = pos[i]; if (!p) return null;
            const dim = focus && !focus.has(i);
            const isHover = hover === i;
            return (
              <g key={n.id} transform={`translate(${p.x} ${p.y})`}
                opacity={dim ? (mode === 'dark' ? 0.22 : 0.3) : 1}
                onMouseEnter={() => setHover(i)}
                onMouseDown={(ev) => { ev.stopPropagation(); drag.current.node = i; drag.current.moved = false; }}
                onMouseUp={() => { if (!drag.current.moved && n.type === 'memory') onSelect(n); drag.current.node = null; }}
                style={{ cursor: n.type === 'memory' ? 'pointer' : 'grab' }}>
                <NodeShape node={n} maxOcc={maxOcc} colorBy={colorBy} hover={isHover} mode={mode} t={t} heavy={heavy} />
                {(n.type === 'memory' || isHover) && (
                  <text y={nodeRadius(n, maxOcc) + 13} textAnchor="middle"
                    fill={dim ? t.textDim : t.text} fontSize={9} fontWeight={isHover ? 600 : 400}
                    style={{ pointerEvents: 'none', letterSpacing: '0.01em' }}>
                    {truncate(n.label, 22)}
                  </text>
                )}
              </g>
            );
          })}
        </g>
      </svg>

      <div className="absolute top-3 right-3 flex gap-1.5">
        {[['+', () => setView((v) => ({ ...v, k: Math.min(3, v.k * 1.2) }))],
          ['−', () => setView((v) => ({ ...v, k: Math.max(0.4, v.k / 1.2) }))],
          ['⤾', () => setView({ x: 0, y: 0, k: 1 })]].map(([lbl, fn], i) => (
          <button key={i} onClick={fn as () => void}
            className={`w-8 h-8 rounded-lg text-sm backdrop-blur-md border transition hover:opacity-80 ${t.glass}`}>
            {lbl as string}
          </button>
        ))}
      </div>
      <Legend colorBy={colorBy} mode={mode} t={t} />
    </div>
  );
}

function nodeRadius(n: MemoryNode, maxOcc: number): number {
  if (n.type === 'org') return 15;
  if (n.type === 'memory') return 7 + 13 * (Math.log((n.occurrences ?? 1) + 1) / Math.log(maxOcc + 1));
  if (n.type === 'decision') return 5.5;
  return 7;
}

function NodeShape({ node, maxOcc, colorBy, hover, mode, t, heavy }: {
  node: MemoryNode; maxOcc: number; colorBy: 'clasif' | 'cluster'; hover: boolean; mode: Mode; t: typeof T[Mode]; heavy: boolean;
}) {
  const r = nodeRadius(node, maxOcc);
  const vetoed = node.governance?.status === 'vetoed';
  const glow = heavy && !hover ? undefined : 'url(#mg-glow)';
  if (node.type === 'memory') {
    const cl = (node.clasif ?? 'publica') as Clasif;
    const fill = colorBy === 'cluster' ? cluster(mode, node.cluster_id) : CLASIF[mode][cl];
    return (
      <>
        <circle r={r + (hover ? 8 : 5)} fill={fill} opacity={hover ? 0.32 : mode === 'dark' ? 0.16 : 0.12} filter={glow} />
        <circle r={r} fill={fill} fillOpacity={0.92}
          stroke={vetoed ? '#ef4444' : hover ? (mode === 'dark' ? '#ffffff' : '#0f172a') : t.nodeStroke}
          strokeWidth={vetoed ? 2 : hover ? 1.5 : 1}
          strokeDasharray={vetoed ? '3 2' : undefined} />
      </>
    );
  }
  if (node.type === 'org')
    return <polygon points={hexagon(r)} fill={t.org} stroke={t.nodeStroke} strokeOpacity={0.6} strokeWidth={1.2} />;
  if (node.type === 'document')
    return <rect x={-r} y={-r * 0.72} width={r * 2} height={r * 1.44} rx={2.5} fill="#0ea5e9" fillOpacity={0.9} stroke="#7dd3fc" strokeOpacity={0.4} />;
  if (node.type === 'conversation')
    return <circle r={r} fill="#22c55e" fillOpacity={0.85} stroke="#86efac" strokeOpacity={0.4} />;
  if (node.type === 'person')
    return <circle r={r} fill={mode === 'dark' ? '#a78bfa' : '#7c3aed'} fillOpacity={0.9} stroke="#ddd6fe" strokeOpacity={0.45} />;
  if (node.type === 'decision')
    return <polygon points={`0,${-r} ${r},0 0,${r} ${-r},0`} fill={mode === 'dark' ? '#f5c518' : '#d99e00'} fillOpacity={0.92} stroke="#fde68a" strokeOpacity={0.5} />;
  return <circle r={r} fill="#94a3b8" />;
}

function hexagon(r: number): string {
  return Array.from({ length: 6 }, (_, i) => {
    const a = (Math.PI / 3) * i - Math.PI / 2;
    return `${(Math.cos(a) * r).toFixed(1)},${(Math.sin(a) * r).toFixed(1)}`;
  }).join(' ');
}
function truncate(s: string, n: number): string {
  return s && s.length > n ? s.slice(0, n) + '…' : s || '';
}

function Legend({ colorBy, mode, t }: { colorBy: 'clasif' | 'cluster'; mode: Mode; t: typeof T[Mode] }) {
  const items = colorBy === 'cluster'
    ? [{ c: CLUSTER[mode][0], t: 'cluster' }, { c: '#0ea5e9', t: 'documento' }, { c: mode === 'dark' ? '#f5c518' : '#d99e00', t: 'decisión' }, { c: '#ec4899', t: 'similitud' }]
    : [{ c: CLASIF[mode].interna, t: 'memoria' }, { c: '#0ea5e9', t: 'documento' }, { c: mode === 'dark' ? '#a78bfa' : '#7c3aed', t: 'persona' }, { c: mode === 'dark' ? '#f5c518' : '#d99e00', t: 'decisión' }, { c: t.org, t: 'org' }];
  return (
    <div className={`absolute bottom-3 left-3 flex flex-wrap gap-x-3 gap-y-1 text-[10px] rounded-xl px-3 py-1.5 backdrop-blur-md border ${t.glass}`}>
      {items.map((i) => (
        <span key={i.t} className="inline-flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full" style={{ background: i.c, boxShadow: `0 0 6px ${i.c}` }} /> {i.t}
        </span>
      ))}
    </div>
  );
}
